import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Clock, Loader2, Send } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'OK', description: t.about.formSuccess });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Hata', description: t.about.formError });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "bg-white border-zinc-300 text-zinc-900 focus-visible:ring-orange-500 focus-visible:ring-offset-0";

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-orange-500/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 relative">
          <h1 className="text-4xl md:text-5xl font-bold text-zinc-900">{t.about.title}</h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t.about.subtitle}</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-2xl overflow-hidden border border-zinc-200 shadow-sm">
            <img src="https://images.pexels.com/photos/6528787/pexels-photo-6528787.jpeg" alt="SLA studio" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className="space-y-4 text-zinc-700 leading-relaxed">
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
          </div>
        </div>
      </section>

      <section className="py-10 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-5">
          <Card className="bg-white border-zinc-200 p-7 shadow-sm">
            <div className="text-orange-600 text-xs uppercase tracking-widest mb-2">01</div>
            <h3 className="text-2xl font-bold text-zinc-900">{t.about.missionTitle}</h3>
            <p className="mt-3 text-zinc-600">{t.about.missionDesc}</p>
          </Card>
          <Card className="bg-white border-zinc-200 p-7 shadow-sm">
            <div className="text-orange-600 text-xs uppercase tracking-widest mb-2">02</div>
            <h3 className="text-2xl font-bold text-zinc-900">{t.about.visionTitle}</h3>
            <p className="mt-3 text-zinc-600">{t.about.visionDesc}</p>
          </Card>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900">{t.about.contactTitle}</h2>
            <p className="mt-3 text-zinc-600">{t.about.contactSubtitle}</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <ContactItem icon={MapPin}  title={t.about.address} value="İstanbul, Türkiye" />
              <ContactItem icon={Phone}   title={t.about.phone}   value="+90 545 453 59 49" />
              <ContactItem icon={Phone}   title={t.about.phone}   value="+90 532 478 02 73" />
              <ContactItem icon={Mail}    title={t.about.email}   value="info@rolviadesign.com" />
              <ContactItem icon={Clock}   title={t.about.hours}   value={t.about.hoursText} />
            </div>
            <Card className="lg:col-span-2 bg-white border-zinc-200 p-6 shadow-sm">
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.about.formName} *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
                </div>
                <div>
                  <Label className="text-zinc-700 mb-2 block">{t.about.formEmail} *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700 mb-2 block">{t.about.formSubject}</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-zinc-700 mb-2 block">{t.about.formMessage} *</Label>
                  <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} required />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={submitting} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0 h-11 px-6">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    {t.about.formSend}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

const ContactItem = ({ icon: Icon, title, value }) => (
  <Card className="bg-white border-zinc-200 p-5 flex items-start gap-3 shadow-sm">
    <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-orange-500" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-widest text-zinc-500">{title}</div>
      <div className="text-zinc-900 mt-1">{value}</div>
    </div>
  </Card>
);

export default About;
